import type { StaticInfoType } from '../general';

import type {
  PositionOnlyHotel,
  DestinationHotel,
  DetailsHotel,
  NameOnlyHotel,
  OverviewActivity,
  OverviewHeartedHotel,
  OverviewHotel,
  MainPolygonOnlyHotel,
  ActivityStaticInfo,
  NeighborhoodPolygonsOnlyHotel,
} from './hotel';

export interface GetHotelsResponseMap {
  [StaticInfoType.Overview]: OverviewHotelGetHotelsResponse;
  [StaticInfoType.OverviewActivity]: OverviewActivityGetHotelsResponse;
  [StaticInfoType.OverviewHeartedHotel]: OverviewHeartedHotelsGetHotelsResponse;
  [StaticInfoType.Destination]: DestinationGetHotelsResponse;
  [StaticInfoType.Details]: DetailsGetHotelsResponse;
  [StaticInfoType.NameOnly]: NameOnlyGetHotelsResponse;
  [StaticInfoType.MainPolygonOnly]: MainPolygonOnlyGetHotelsResponse;
  [StaticInfoType.PositionOnly]: PositionOnlyGetHotelsResponse;
  [StaticInfoType.NeighborhoodPolygonsOnly]: NeighborhoodPolygonsOnlyGetHotelsResponse;
  [StaticInfoType.ActivitySuggestions]: ActivitySuggestionsGetHotelsResponse;
}

export interface StaticInfoResponse<T extends StaticInfoType> {
  response: GetHotelsResponseMap[T];
  type: T;
}

export type GetHotelsResponse =
  | OverviewHotelGetHotelsResponse
  | OverviewActivityGetHotelsResponse
  | OverviewHeartedHotelsGetHotelsResponse
  | DestinationGetHotelsResponse
  | DetailsGetHotelsResponse;

export interface OverviewHotelGetHotelsResponse {
  hotels: OverviewHotel[];
  polygons: Polygon[];
}

export interface OverviewActivityGetHotelsResponse {
  hotels: OverviewActivity[];
}

export interface OverviewHeartedHotelsGetHotelsResponse {
  hotels: OverviewHeartedHotel[];
  polygons: Polygon[];
}

export interface DestinationGetHotelsResponse {
  hotels: DestinationHotel[];
  polygons: Polygon[];
}

export interface DetailsGetHotelsResponse {
  hotels: DetailsHotel[];
  polygons: Polygon[];
  tagInformation: TagInformation[];
}

export interface NameOnlyGetHotelsResponse {
  hotels: NameOnlyHotel[];
}

export interface MainPolygonOnlyGetHotelsResponse {
  hotels: MainPolygonOnlyHotel[];
}

export interface PositionOnlyGetHotelsResponse {
  hotels: PositionOnlyHotel[];
}

export interface NeighborhoodPolygonsOnlyGetHotelsResponse {
  hotels: [];
  neighborhoodPolygons: NeighborhoodPolygonsOnlyHotel[] | null;
}

export interface ActivitySuggestionsGetHotelsResponse {
  hotels: ActivityStaticInfo[];
}

export interface Polygon {
  polygonId: number;
  fullName: string;
  type: PolygonType;
}

export enum PolygonType {
  city,
  province_state,
  high_level_region,
  country,
  neighborhood,
}

export interface TagInformation {
  tag: string;
  icon?: Icon;
}

interface Icon {
  imageUrl: string;
}

export enum HotelAmenityType {
  IsAdultsOnly = 'isAdultsOnly',
  HasAirConditioning = 'hasAirConditioning',
  HasAllInclusive = 'hasAllInclusive',
  HasBalcony = 'hasBalcony',
  HasBalconyOrTerrace = 'hasBalconyOrTerrace',
  HasBar = 'hasBar',
  HasBathtubOrShower = 'hasBathtubOrShower',
  HasBoutique = 'hasBoutique',
  HasCableOrSatteliteTelevision = 'hasCableOrSatteliteTelevision',
  HasCarpet = 'hasCarpet',
  HasCeilingFan = 'hasCeilingFan',
  HasChildBeds = 'hasChildBeds',
  HasChildChairs = 'hasChildChairs',
  HasChildrensPool = 'hasChildrensPool',
  HasCoffeeMaker = 'hasCoffeeMaker',
  HasConferenceRoom = 'hasConferenceRoom',
  HasDesk = 'hasDesk',
  HasDishWasher = 'hasDishWasher',
  HasDryer = 'hasDryer',
  HasElectricCarCharger = 'hasElectricCarCharger',
  HasEngineHeater = 'hasEngineHeater',
  HasFirePlace = 'hasFirePlace',
  HasFreezer = 'hasFreezer',
  HasGarage = 'hasGarage',
  HasGarden = 'hasGarden',
  HasGym = 'hasGym',
  HasHairDryer = 'hasHairDryer',
  HasInternet = 'hasInternet',
  HasIron = 'hasIron',
  HasIroningBoard = 'hasIroningBoard',
  HasJacuzzi = 'hasJacuzzi',
  HasKettle = 'hasKettle',
  HasLakeView = 'hasLakeView',
  HasMicroWaveOven = 'hasMicroWaveOven',
  HasMiniBar = 'hasMiniBar',
  HasMountainView = 'hasMountainView',
  HasOven = 'hasOven',
  HasParking = 'hasParking',
  HasPool = 'hasPool',
  HasPorch = 'hasPorch',
  HasSafetyBox = 'hasSafetyBox',
  HasSauna = 'hasSauna',
  HasSeaView = 'hasSeaView',
  HasShower = 'hasShower',
  HasSkiInSkiOut = 'hasSkiInSkiOut',
  HasSpa = 'hasSpa',
  HasSunbeds = 'hasSunbeds',
  HasTelephone = 'hasTelephone',
  HasTelevision = 'hasTelevision',
  HasTennis = 'hasTennis',
  HasTerrace = 'hasTerrace',
  HasToaster = 'hasToaster',
  HasVineyard = 'hasVineyard',
  HasWashingMachine = 'hasWashingMachine',
  IsBusinessProperty = 'isBusinessProperty',
  IsFamilyProperty = 'isFamilyProperty',
  IsGreenSustainableProperty = 'isGreenSustainableProperty',
  IsSmokingAllowed = 'isSmokingAllowed',
  IsSmokingDisallowed = 'isSmokingDisallowed',
  IsSunnySide = 'isSunnySide',
  // NOTE: Content API has typo in field name
  IsCastleOrMansion = 'isCastleOrManison',
  PetsAreAllowed = 'petsAreAllowed',
  PetsAreNotAllowed = 'petsAreNotAllowed',
}

export type HotelAmenities = Record<HotelAmenityType, true | null>;

export interface GuestRatingBreakdown {
  count: number | null;
  overall: number | null;
  cleanliness: number | null;
  service: number | null;
  comfort: number | null;
  condition: number | null;
  location: number | null;
  neighborhood: number | null;
  quality: number | null;
  value: number | null;
  amenities: number | null;
  recommendationPercent: number | null;
}
